class EventsLoader extends HTMLElement {
  constructor() {
    super();
    this.visibleEvents = new Set();
    this.baseUrl = this.getAttribute('base-url') || '';
    this.fixedTypes = this.getAttribute('data-fixed-types')?.split(',').filter(Boolean) || [];
    this.fixedTopics = this.getAttribute('data-fixed-topics')?.split(',').filter(Boolean) || [];
    this.selectedOptions = [...this.fixedTypes, ...this.fixedTopics];
  }

  updateSelectedOptions(selectedOptions) {
    this.selectedOptions = [
      ...this.fixedTypes,
      ...this.fixedTopics,
      ...selectedOptions.filter(option => 
        !this.fixedTypes.includes(option) && !this.fixedTopics.includes(option)
      )
    ];
  }

  connectedCallback() {
    this.initializeComponent();
  }

  initializeComponent() {
    this.setInitiallyVisibleEvents();
    this.setupEventListeners();
  }

  setInitiallyVisibleEvents() {
    const initialIds = this.getAttribute('initial-ids');
    if (initialIds) {
      initialIds.split(',').forEach((id) => this.visibleEvents.add(id.trim()));
    } else {
      this.loadMore({ isReset: true });
    }
  }

  setupEventListeners() {
    this.addEventListener('filter-changed', (e) => {
      e.stopPropagation();
      this.handleFilterChange(e);
    });
    this.addEventListener('filter-initialized', (e) => {
      e.stopPropagation();
      this.handleFilterInitialization(e);
    });
    const loadMoreButton = this.querySelector('[name="load-more"]');
    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', () => this.loadMore());
    }
  }

  handleFilterChange(event) {
    const { selectedOptions } = event.detail;
    this.updateSelectedOptions(selectedOptions);
    this.loadMore({ isReset: true });
  }

  handleFilterInitialization(event) {
    const { selectedOptions } = event.detail;
    this.updateSelectedOptions(selectedOptions);
  }

  async loadMore(options = {}) {
    try {
      const formData = this.prepareFormData();
      const response = await this.fetchEvents(formData);
      this.handleResponse(response, options.isReset);
    } catch (error) {
      this.handleLoadError(error);
    }
  }

  prepareFormData() {
    const formData = new FormData();
    formData.append('CEUid', this.getAttribute('ceuid'));
    formData.append('eventsIDs', Array.from(this.visibleEvents).join(','));
    formData.append('doktypenames', this.fixedTypes.join(','));
    formData.append('sortingfield', this.getAttribute('data-sortingfield') || 'inputdate');
    formData.append('sortingorder', this.getAttribute('data-sortingorder') || 'ASC');
    formData.append('maxItems', this.getAttribute('data-max-items') || '12');
    this.selectedOptions.forEach((option) => formData.append('selectedoptions[]', option));
    formData.append('languageUid', this.getAttribute('language-uid'));
    
    return formData;
  }

  async fetchEvents(formData) {
    const baseUrlTrimmed = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl;
    const url = `${baseUrlTrimmed}?eventsList=1&filter=1`;
    const response = await fetch(url, { method: 'POST', body: formData });
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return response.json();
  }

  handleResponse(result, isReset) {
    const { maxLength, previewContent } = result;

    if (isReset) {
      this.visibleEvents.clear();
      this.dispatchEvent(
        new CustomEvent('reset-events', {
          bubbles: false,
          composed: false
        }),
      );
    }

    this.dispatchEvents(previewContent);
    this.updateLoadMoreStatus(previewContent.length, maxLength);
  }

  dispatchEvents(events) {
    events.forEach((event) => {
      if (!this.visibleEvents.has(event.uid)) {
        this.visibleEvents.add(event.uid);
        this.dispatchEvent(
          new CustomEvent('new-event', {
            detail: event,
            bubbles: false,
            composed: false
          }),
        );
      }
    });
  }

  updateLoadMoreStatus(contentLength, maxLength) {
    const loadMoreButton = this.querySelector('[name="load-more"]');
    if (loadMoreButton) {
      loadMoreButton.style.display = contentLength <= maxLength ? 'block' : 'none';
    }
  }

  handleLoadError(error) {
    console.error('Error:', error);
    this.dispatchEvent(
      new CustomEvent('events-load-error', {
        detail: error,
        bubbles: false,
        composed: false
      }),
    );
  }
}

customElements.define('events-loader', EventsLoader); 